import React from "react";
import {
  RiFilter3Line,
  RiArrowDownSLine,
  RiCheckLine,
  RiCalendarLine,
  RiPriceTag3Line,
  RiCloseLine,
} from "react-icons/ri";
import {
  DateFilterType,
  DateFilterOption,
  InvoiceFilterType,
  TypeFilterOption,
  InvoiceStatusOption,
} from "../types";
import { formatStatusColor } from "../utils";

interface StatementFilterToolbarProps {
  onlyOutstanding: boolean;
  setOnlyOutstanding: (val: boolean) => void;
  // Date filter
  dateFilter: DateFilterType;
  setDateFilter: (val: DateFilterType) => void;
  customStartDate: string;
  setCustomStartDate: (val: string) => void;
  customEndDate: string;
  setCustomEndDate: (val: string) => void;
  isDateDropdownOpen: boolean;
  setIsDateDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dateDropdownRef: React.RefObject<HTMLDivElement>;
  dateFilterOptions: DateFilterOption[];
  activeDateLabel: string;
  handleClearCustomDates: () => void;
  // Status filter
  invoiceStatusFilters: string[];
  setInvoiceStatusFilters: React.Dispatch<React.SetStateAction<string[]>>;
  isStatusDropdownOpen: boolean;
  setIsStatusDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  statusDropdownRef: React.RefObject<HTMLDivElement>;
  availableStatuses: InvoiceStatusOption[];
  statusCounts: Record<string, number>;
  handleSelectStatus: (statusLabel: string) => void;
  totalInvoicesCount: number;
  // Type filter
  invoiceTypeFilter: InvoiceFilterType;
  setInvoiceTypeFilter: (val: InvoiceFilterType) => void;
  isTypeDropdownOpen: boolean;
  setIsTypeDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  typeDropdownRef: React.RefObject<HTMLDivElement>;
  typeFilterOptions: TypeFilterOption[];
  activeTypeOption: TypeFilterOption;
  // Search
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  tr: (key: string) => string;
}

export default function StatementFilterToolbar({
  onlyOutstanding,
  setOnlyOutstanding,
  dateFilter,
  setDateFilter,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  isDateDropdownOpen,
  setIsDateDropdownOpen,
  dateDropdownRef,
  dateFilterOptions,
  activeDateLabel,
  handleClearCustomDates,
  invoiceStatusFilters,
  setInvoiceStatusFilters,
  isStatusDropdownOpen,
  setIsStatusDropdownOpen,
  statusDropdownRef,
  availableStatuses,
  statusCounts,
  handleSelectStatus,
  totalInvoicesCount,
  invoiceTypeFilter,
  setInvoiceTypeFilter,
  isTypeDropdownOpen,
  setIsTypeDropdownOpen,
  typeDropdownRef,
  typeFilterOptions,
  activeTypeOption,
  searchTerm,
  setSearchTerm,
  tr,
}: StatementFilterToolbarProps) {
  const ActiveTypeIcon = activeTypeOption.icon;

  return (
    <div className="no-print bg-slate-50 border-b border-slate-200 px-6 py-3 flex items-center justify-between flex-wrap gap-3 shrink-0">
      <div className="flex items-center gap-4 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyOutstanding}
            onChange={(e) => setOnlyOutstanding(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
          <span>{tr("Restrict to outstanding invoices only")}</span>
        </label>
      </div>

      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Date Filter Dropdown */}
        <div className="relative" ref={dateDropdownRef}>
          <button
            type="button"
            onClick={() => setIsDateDropdownOpen((prev) => !prev)}
            aria-expanded={isDateDropdownOpen}
            aria-haspopup="listbox"
            aria-label={tr("Filter by date")}
            data-testid="statement-date-filter-trigger"
            className={`inline-flex items-center justify-between gap-2 px-3 py-1.5 min-h-[32px] text-xs font-medium bg-white text-slate-700 border rounded-lg shadow-sm transition-all duration-150 cursor-pointer select-none hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 ${
              isDateDropdownOpen || dateFilter !== "all"
                ? "border-blue-500 ring-2 ring-blue-500/20 bg-slate-50/80"
                : "border-slate-300"
            }`}
          >
            <span className="flex items-center gap-1.5 truncate max-w-[180px]">
              <RiCalendarLine className="text-sm text-blue-600 shrink-0" />
              <span className="font-semibold text-slate-800 truncate">{activeDateLabel}</span>
            </span>
            <RiArrowDownSLine
              className={`text-slate-400 text-sm transition-transform duration-200 ${
                isDateDropdownOpen ? "rotate-180 text-blue-600" : ""
              }`}
            />
          </button>

          {isDateDropdownOpen && (
            <div
              role="listbox"
              aria-label={tr("Filter by date")}
              className="absolute right-0 sm:left-0 top-full mt-1.5 z-50 w-72 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 space-y-0.5 animate-in fade-in zoom-in-95 duration-100 ring-1 ring-black/5"
            >
              <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {tr("Filter by Date")}
              </div>
              {dateFilterOptions.map((opt) => {
                const isSelected = opt.value === dateFilter;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`date-filter-option-${opt.value}`}
                    onClick={() => {
                      setDateFilter(opt.value);
                      setIsDateDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-blue-50/80 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900 font-normal"
                    }`}
                  >
                    <div>
                      <div className="leading-tight">{opt.label}</div>
                      {opt.description && (
                        <div
                          className={`text-[10px] leading-snug mt-0.5 ${
                            isSelected ? "text-blue-500 font-normal" : "text-slate-400"
                          }`}
                        >
                          {opt.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <RiCheckLine className="text-blue-600 text-base shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Inline Custom Date inputs in toolbar when Custom range is active */}
        {dateFilter === "custom" && (
          <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-lg px-2 py-1 shadow-sm text-xs">
            <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
              {tr("Range")}:
            </span>
            <input
              type="date"
              aria-label={tr("Start Date")}
              data-testid="statement-toolbar-start-date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="text-xs text-slate-700 bg-transparent focus:outline-none cursor-pointer w-24"
            />
            <span className="text-slate-400 font-medium">–</span>
            <input
              type="date"
              aria-label={tr("End Date")}
              data-testid="statement-toolbar-end-date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="text-xs text-slate-700 bg-transparent focus:outline-none cursor-pointer w-24"
            />
            {(customStartDate || customEndDate) && (
              <button
                type="button"
                onClick={handleClearCustomDates}
                aria-label={tr("Clear custom dates")}
                className="text-slate-400 hover:text-slate-700 p-0.5 rounded cursor-pointer"
              >
                <RiCloseLine className="text-sm" />
              </button>
            )}
          </div>
        )}

        {/* Status Filter Dropdown */}
        <div className="relative" ref={statusDropdownRef}>
          <div
            onClick={() => setIsStatusDropdownOpen((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 px-2 py-1 min-h-[32px] max-w-[360px] text-xs font-medium bg-white text-slate-700 border rounded-lg shadow-sm transition-all duration-150 cursor-pointer ${
              isStatusDropdownOpen || invoiceStatusFilters.length > 0
                ? "border-blue-500 ring-2 ring-blue-500/20 bg-slate-50/80"
                : "border-slate-300"
            }`}
          >
            <RiPriceTag3Line className="text-sm text-slate-500 shrink-0" />
            <div className="flex items-center gap-1 overflow-x-auto">
              {invoiceStatusFilters.length === 0 ? (
                <span className="font-semibold text-slate-800 whitespace-nowrap px-0.5">
                  {tr("All Statuses")}
                </span>
              ) : (
                invoiceStatusFilters.map((status) => (
                  <span
                    key={status}
                    data-testid={`status-filter-chip-${status.toLowerCase()}`}
                    className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 whitespace-nowrap"
                  >
                    {tr(status)}
                    <button
                      type="button"
                      aria-label={`${tr("Remove")} ${tr(status)} ${tr("status filter")}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectStatus(status);
                      }}
                      className="rounded-full text-blue-500 hover:text-blue-800 cursor-pointer"
                    >
                      <RiCloseLine className="text-xs" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <button
              type="button"
              aria-expanded={isStatusDropdownOpen}
              aria-haspopup="listbox"
              aria-label={tr("Filter invoice status")}
              data-testid="statement-status-filter-trigger"
              className="shrink-0 p-0.5 rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            >
              <RiArrowDownSLine
                className={`text-slate-400 text-sm transition-transform duration-200 ${
                  isStatusDropdownOpen ? "rotate-180 text-blue-600" : ""
                }`}
              />
            </button>
          </div>

          {isStatusDropdownOpen && (
            <div
              role="listbox"
              aria-multiselectable="true"
              aria-label={tr("Filter invoice status")}
              className="absolute right-0 sm:left-0 top-full mt-1.5 z-50 w-56 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 space-y-0.5 animate-in fade-in zoom-in-95 duration-100 ring-1 ring-black/5 max-h-72 overflow-y-auto"
            >
              <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {tr("Filter by Status")}
              </div>

              {/* All Statuses Option */}
              <button
                type="button"
                role="option"
                aria-selected={invoiceStatusFilters.length === 0}
                data-testid="status-filter-option-all"
                onClick={() => {
                  setInvoiceStatusFilters([]);
                  setIsStatusDropdownOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                  invoiceStatusFilters.length === 0
                    ? "bg-blue-50/80 text-blue-700 font-semibold"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900 font-normal"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                  <span>{tr("All Statuses")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex w-4 shrink-0 justify-center">
                    {invoiceStatusFilters.length === 0 && (
                      <RiCheckLine className="text-blue-600 text-base" />
                    )}
                  </span>
                  <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                    {totalInvoicesCount}
                  </span>
                </div>
              </button>

              {/* Individual Status Options */}
              {availableStatuses.map((st) => {
                const isSelected = invoiceStatusFilters.some(
                  (status) => status.toLowerCase() === st.label.toLowerCase()
                );
                const count = statusCounts[st.label.toLowerCase()] || 0;
                return (
                  <button
                    key={st.label}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`status-filter-option-${st.label.toLowerCase()}`}
                    onClick={() => handleSelectStatus(st.label)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-blue-50/80 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900 font-normal"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: `rgb(${formatStatusColor(st.color)})` || "#94a3b8" }}
                      />
                      <span>{tr(st.label)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex w-4 shrink-0 justify-center">
                        {isSelected && (
                          <RiCheckLine className="text-blue-600 text-base" />
                        )}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                        {count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Custom Styled Type Filter Dropdown */}
        <div className="relative" ref={typeDropdownRef}>
          <button
            type="button"
            onClick={() => setIsTypeDropdownOpen((prev) => !prev)}
            aria-expanded={isTypeDropdownOpen}
            aria-haspopup="listbox"
            aria-label={tr("Filter invoice type")}
            className={`inline-flex items-center justify-between gap-2 px-3 py-1.5 min-h-[32px] text-xs font-medium bg-white text-slate-700 border rounded-lg shadow-sm transition-all duration-150 cursor-pointer select-none hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 ${
              isTypeDropdownOpen
                ? "border-blue-500 ring-2 ring-blue-500/20 bg-slate-50/80"
                : "border-slate-300"
            }`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <ActiveTypeIcon className={`text-sm ${activeTypeOption.iconColor}`} />
              <span className="font-semibold text-slate-800">{activeTypeOption.label}</span>
            </span>
            <RiArrowDownSLine
              className={`text-slate-400 text-sm transition-transform duration-200 ${
                isTypeDropdownOpen ? "rotate-180 text-blue-600" : ""
              }`}
            />
          </button>

          {isTypeDropdownOpen && (
            <div
              role="listbox"
              aria-label={tr("Filter invoice type")}
              className="absolute right-0 sm:left-0 top-full mt-1.5 z-50 w-56 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 space-y-0.5 animate-in fade-in zoom-in-95 duration-100 ring-1 ring-black/5"
            >
              <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {tr("Filter by Type")}
              </div>
              {typeFilterOptions.map((opt) => {
                const isSelected = opt.value === invoiceTypeFilter;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`filter-option-${opt.value}`}
                    onClick={() => {
                      setInvoiceTypeFilter(opt.value);
                      setIsTypeDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-blue-50/80 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900 font-normal"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`p-1.5 rounded-md flex items-center justify-center shrink-0 ${
                          isSelected
                            ? "bg-blue-100 text-blue-700"
                            : `${opt.bgColor} ${opt.iconColor}`
                        }`}
                      >
                        <Icon className="text-sm" />
                      </div>
                      <div>
                        <div className="leading-tight">{opt.label}</div>
                        {opt.description && (
                          <div
                            className={`text-[10px] leading-snug mt-0.5 ${
                              isSelected ? "text-blue-500 font-normal" : "text-slate-400"
                            }`}
                          >
                            {opt.description}
                          </div>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <RiCheckLine className="text-blue-600 text-base shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Search Box */}
        <div className="relative">
          <input
            type="text"
            placeholder={tr("Search invoices, requests...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-56 bg-white text-slate-700 placeholder-slate-400 shadow-sm"
          />
          <RiFilter3Line className="absolute left-2.5 top-2 text-slate-400 text-sm" />
        </div>
      </div>
    </div>
  );
}
